import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Box,
  Typography,
  InputAdornment,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InboxIcon from '@mui/icons-material/Inbox';
import SendIcon from '@mui/icons-material/Send';
import DraftsIcon from '@mui/icons-material/Drafts';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import LabelIcon from '@mui/icons-material/Label';
import ReportIcon from '@mui/icons-material/Report';
import ArchiveIcon from '@mui/icons-material/Archive';
import EmailIcon from '@mui/icons-material/Email';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ContactsIcon from '@mui/icons-material/Contacts';
import CloudIcon from '@mui/icons-material/Cloud';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import { useEmailStore } from '@/store/emailStore';
import { contactsClient } from '@/services/contactsClient';
import { useAuthStore } from '@/store/authStore';
import type { Contact } from '@/types/api';
import type { Email, Mailbox } from '@/types/jmap.types';
import { formatRelativeShort } from '@/utils/relativeTime';

type ItemKind = 'action' | 'page' | 'mailbox' | 'email' | 'contact';

interface CommandItem {
  id: string;
  kind: ItemKind;
  label: string;
  /** Secondary text shown to the right or below. */
  secondary?: string;
  /** Lowercase haystack used for matching. */
  search: string;
  icon: React.ReactNode;
  onSelect: () => void;
  /** When set, the score floor — items pinned to the top regardless of match. */
  pin?: number;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

const KIND_ORDER: ItemKind[] = ['action', 'email', 'contact', 'mailbox', 'page'];
const KIND_LABEL: Record<ItemKind, string> = {
  action: 'Actions',
  email: 'Emails',
  contact: 'Contacts',
  mailbox: 'Mailboxes',
  page: 'Pages',
};

/**
 * Pick a sensible icon for a mailbox by its JMAP role.
 */
function mailboxIcon(role: string | null): React.ReactNode {
  switch (role) {
    case 'inbox':   return <InboxIcon fontSize="small" />;
    case 'sent':    return <SendIcon fontSize="small" />;
    case 'drafts':  return <DraftsIcon fontSize="small" />;
    case 'trash':   return <DeleteIcon fontSize="small" />;
    case 'archive': return <ArchiveIcon fontSize="small" />;
    case 'spam':    return <ReportIcon fontSize="small" />;
    case 'flagged': return <StarIcon fontSize="small" />;
    default:        return <LabelIcon fontSize="small" />;
  }
}

function fromName(email: Email): string {
  const f = email.from?.[0];
  if (!f) return '(unknown sender)';
  return f.name || f.email || '(unknown sender)';
}

function fromHaystack(email: Email): string {
  const subject = (email.subject || '').toLowerCase();
  const senders = (email.from || [])
    .map(a => `${a.name || ''} ${a.email || ''}`)
    .join(' ')
    .toLowerCase();
  const recipients = [...(email.to || []), ...(email.cc || [])]
    .map(a => `${a.name || ''} ${a.email || ''}`)
    .join(' ')
    .toLowerCase();
  const preview = (email.preview || '').toLowerCase();
  return `${subject} ${senders} ${recipients} ${preview}`;
}

/**
 * ⌘K palette for webmail. Searches across:
 *  - quick actions (Compose)
 *  - already-loaded emails (from the current mailbox + cached lists)
 *  - personal contacts (lazy-loaded once on first open)
 *  - mailboxes
 *  - top-level pages
 *
 * Email search is client-side over what's loaded — it doesn't fan out to a
 * JMAP `Email/query`. That keeps it instant; for deep search the user can
 * still use the regular search bar in the email list.
 */
export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const mailboxes = useEmailStore((s) => s.mailboxes);
  const emails = useEmailStore((s) => s.emails);
  const emailListCache = useEmailStore((s) => s.emailListCache);
  const selectMailbox = useEmailStore((s) => s.selectMailbox);
  const selectEmail = useEmailStore((s) => s.selectEmail);

  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Lazy-load contacts the first time the palette opens. Subsequent opens
  // reuse the cached list — contacts rarely change inside a session.
  useEffect(() => {
    if (!open || contactsLoaded || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await contactsClient.listContacts(token);
        if (!cancelled) {
          setContacts(list);
          setContactsLoaded(true);
        }
      } catch {
        // Best-effort — palette still works without contacts.
        if (!cancelled) setContactsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [open, contactsLoaded, token]);

  // Pool of all emails the palette can search: the currently-rendered list
  // plus everything cached for other mailboxes. Deduped by id.
  const searchableEmails: Email[] = useMemo(() => {
    const seen = new Map<string, Email>();
    for (const e of emails) seen.set(e.id, e);
    for (const entry of Object.values(emailListCache)) {
      for (const e of entry.emails) {
        if (!seen.has(e.id)) seen.set(e.id, e);
      }
    }
    // Keep at most ~500 most-recent emails — searching thousands is wasteful
    // on every keystroke. JMAP queries handle deeper search.
    const all = Array.from(seen.values());
    all.sort((a, b) => {
      const ta = a.receivedAt ? new Date(a.receivedAt).getTime() : 0;
      const tb = b.receivedAt ? new Date(b.receivedAt).getTime() : 0;
      return tb - ta;
    });
    return all.slice(0, 500);
  }, [emails, emailListCache]);

  const items: CommandItem[] = useMemo(() => {
    const actions: CommandItem[] = [
      {
        id: 'action:compose',
        kind: 'action',
        label: 'Compose new email',
        secondary: 'Open a new draft',
        search: 'compose new email write draft',
        icon: <EditIcon fontSize="small" />,
        onSelect: () => {
          // Mailbox listens for this and opens its composer. If we're not on
          // /, navigate there first; the listener fires after mount.
          if (window.location.pathname !== '/') {
            navigate('/');
            setTimeout(() => window.dispatchEvent(new CustomEvent('webmail:compose')), 50);
          } else {
            window.dispatchEvent(new CustomEvent('webmail:compose'));
          }
        },
      },
    ];

    const pages: CommandItem[] = [
      { id: 'page:mail',       kind: 'page', label: 'Mail',       secondary: 'Inbox',       search: 'mail inbox home',           icon: <EmailIcon fontSize="small" />,          onSelect: () => navigate('/') },
      { id: 'page:calendar',   kind: 'page', label: 'Calendar',   secondary: 'Events',      search: 'calendar events meetings',  icon: <CalendarMonthIcon fontSize="small" />,  onSelect: () => navigate('/calendar') },
      { id: 'page:contacts',   kind: 'page', label: 'Contacts',   secondary: 'Address book', search: 'contacts people address',  icon: <ContactsIcon fontSize="small" />,       onSelect: () => navigate('/contacts') },
      { id: 'page:drive',      kind: 'page', label: 'Drive',      secondary: 'Files',       search: 'drive files storage',       icon: <CloudIcon fontSize="small" />,          onSelect: () => navigate('/drive') },
      { id: 'page:settings',   kind: 'page', label: 'Settings',   secondary: 'Preferences', search: 'settings preferences config', icon: <SettingsIcon fontSize="small" />,    onSelect: () => navigate('/settings') },
    ];

    const mailboxItems: CommandItem[] = mailboxes.map((m: Mailbox) => ({
      id: `mailbox:${m.id}`,
      kind: 'mailbox',
      label: m.name,
      secondary: m.unreadEmails > 0 ? `${m.unreadEmails} unread` : undefined,
      search: `${m.name} ${m.role || ''} mailbox folder`.toLowerCase(),
      icon: mailboxIcon(m.role),
      onSelect: () => {
        if (window.location.pathname !== '/') navigate('/');
        selectMailbox(m.id);
      },
    }));

    const emailItems: CommandItem[] = searchableEmails.slice(0, 200).map((e) => ({
      id: `email:${e.id}`,
      kind: 'email',
      label: e.subject || '(no subject)',
      secondary: `${fromName(e)}${e.receivedAt ? ' · ' + formatRelativeShort(new Date(e.receivedAt)) : ''}`,
      search: fromHaystack(e),
      icon: <EmailIcon fontSize="small" />,
      onSelect: () => {
        // Pick the first mailbox this email lives in so the list scopes correctly.
        const mailboxId = Object.keys(e.mailboxIds || {})[0];
        if (window.location.pathname !== '/') navigate('/');
        if (mailboxId) selectMailbox(mailboxId);
        selectEmail(e.id);
      },
    }));

    const contactItems: CommandItem[] = contacts.map((c) => ({
      id: `contact:${c.id}`,
      kind: 'contact',
      label: c.displayName || c.email,
      // Show the email as the secondary line so the user always sees what
      // address they're about to compose to — no surprise sends.
      secondary: c.displayName ? c.email : (c.company || c.jobTitle || undefined),
      search: `${c.displayName || ''} ${c.email} ${c.firstName || ''} ${c.lastName || ''} ${c.company || ''}`.toLowerCase(),
      icon: <PersonIcon fontSize="small" />,
      onSelect: () => {
        // Selecting a contact = compose a new email to them. This is the
        // most-asked-for action; the alternative (jump to Contacts page) is
        // still reachable via the page entry under "Pages".
        const dispatchCompose = () =>
          window.dispatchEvent(new CustomEvent('webmail:compose', { detail: { to: [c.email] } }));
        if (window.location.pathname !== '/') {
          navigate('/');
          setTimeout(dispatchCompose, 50);
        } else {
          dispatchCompose();
        }
      },
    }));

    return [...actions, ...emailItems, ...contactItems, ...mailboxItems, ...pages];
  }, [navigate, mailboxes, searchableEmails, contacts, selectMailbox, selectEmail]);

  const filtered = useMemo(() => {
    const q = query.trim();
    const qLower = q.toLowerCase();
    if (!qLower) {
      // No query: show actions, mailboxes, and pages only — don't drown the
      // user in 200 emails when they've just opened the palette.
      return items.filter((i) => i.kind === 'action' || i.kind === 'mailbox' || i.kind === 'page');
    }
    const scored = items
      .map((item) => {
        const labelLower = item.label.toLowerCase();
        if (labelLower.startsWith(qLower)) return { item, score: 0 };
        if (labelLower.includes(qLower)) return { item, score: 1 };
        if (item.search.includes(qLower)) return { item, score: 2 };
        return null;
      })
      .filter((x): x is { item: CommandItem; score: number } => x !== null);
    // Sort: exact matches first, then by kind precedence so emails surface
    // before page entries when the query plausibly matches both.
    const kindRank: Record<ItemKind, number> = { action: 0, email: 1, contact: 2, mailbox: 3, page: 4 };
    scored.sort((a, b) => a.score - b.score || kindRank[a.item.kind] - kindRank[b.item.kind]);
    const trimmed = scored.slice(0, 80).map((s) => s.item);

    // Always offer a "Search all mail for X" action when the user has typed
    // a query — even when client-side results are zero. This routes the
    // query through the existing JMAP-backed search instead of bouncing the
    // user to a "no results" screen.
    const deepSearch: CommandItem = {
      id: 'action:deep-search',
      kind: 'action',
      label: `Search all mail for "${q}"`,
      secondary: 'Run a full server-side search',
      search: '',
      icon: <SearchIcon fontSize="small" />,
      onSelect: () => {
        if (window.location.pathname !== '/') {
          navigate('/');
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('webmail:set-search', { detail: { query: q } }));
          }, 50);
        } else {
          window.dispatchEvent(new CustomEvent('webmail:set-search', { detail: { query: q } }));
        }
      },
    };
    return [deepSearch, ...trimmed];
  }, [items, query, navigate]);

  // Reset index whenever filtered list changes shape.
  useEffect(() => { setActiveIdx(0); }, [query, open]);
  useEffect(() => { if (!open) setQuery(''); }, [open]);

  // Scroll active item into view.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const node = list.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    node?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  const runActive = useCallback(() => {
    const item = filtered[activeIdx];
    if (!item) return;
    onClose();
    setTimeout(() => item.onSelect(), 0);
  }, [filtered, activeIdx, onClose]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        runActive();
      } else if (e.key === 'Home') {
        e.preventDefault();
        setActiveIdx(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setActiveIdx(Math.max(0, filtered.length - 1));
      }
    },
    [filtered.length, runActive]
  );

  // Group filtered items by kind for display, preserving the global order
  // assigned above (so activeIdx maps to the rendered position).
  const grouped = useMemo(() => {
    const g: Record<ItemKind, CommandItem[]> = { action: [], email: [], contact: [], mailbox: [], page: [] };
    filtered.forEach((it) => g[it.kind].push(it));
    return g;
  }, [filtered]);

  const flatOrder = useMemo(
    () => KIND_ORDER.flatMap((k) => grouped[k]),
    [grouped]
  );

  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { mt: 8, alignSelf: 'flex-start', borderRadius: 2 } }}
      onKeyDown={onKeyDown}
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
    >
      <Box sx={{ p: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField
          inputRef={inputRef}
          autoFocus
          fullWidth
          variant="standard"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search mail, contacts, mailboxes…"
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Chip size="small" label="Esc" sx={{ height: 22, fontSize: 11 }} />
              </InputAdornment>
            ),
            sx: { fontSize: '1rem', py: 0.5 },
          }}
        />
      </Box>

      <DialogContent sx={{ p: 0, maxHeight: 480 }}>
        {filtered.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {query ? `No results for "${query}"` : 'Type to search across mail, contacts, mailboxes, and pages.'}
            </Typography>
          </Box>
        ) : (
          <List ref={listRef} dense disablePadding>
            {KIND_ORDER.map((kind) => {
              const groupItems = grouped[kind];
              if (groupItems.length === 0) return null;
              return (
                <li key={kind}>
                  <ul style={{ padding: 0 }}>
                    <ListSubheader
                      sx={{
                        bgcolor: 'background.paper',
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'text.secondary',
                        lineHeight: 2,
                      }}
                    >
                      {KIND_LABEL[kind]}
                    </ListSubheader>
                    {groupItems.map((item) => {
                      const flatIdx = flatOrder.indexOf(item);
                      const isActive = flatIdx === activeIdx;
                      return (
                        <ListItemButton
                          key={item.id}
                          data-idx={flatIdx}
                          selected={isActive}
                          onMouseMove={() => setActiveIdx(flatIdx)}
                          onClick={() => {
                            setActiveIdx(flatIdx);
                            runActive();
                          }}
                          sx={{ px: 2, py: 0.75 }}
                        >
                          <Box sx={{ mr: 1.5, color: 'text.secondary', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                            {item.icon}
                          </Box>
                          <ListItemText
                            primary={item.label}
                            secondary={item.secondary}
                            primaryTypographyProps={{
                              variant: 'body2',
                              fontWeight: 500,
                              noWrap: true,
                            }}
                            secondaryTypographyProps={{
                              variant: 'caption',
                              noWrap: true,
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
          </List>
        )}
      </DialogContent>

      <Box
        sx={{
          p: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 2,
          color: 'text.secondary',
          alignItems: 'center',
        }}
      >
        <Typography variant="caption">↑↓ navigate</Typography>
        <Typography variant="caption">↵ open</Typography>
        <Typography variant="caption">Esc close</Typography>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption">{isMac ? '⌘K' : 'Ctrl+K'} toggles</Typography>
      </Box>
    </Dialog>
  );
}
